import { useState } from 'react';
import { axiosInstance } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const QuickNearMissReport = ({ open, onOpenChange, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    whatAlmostHappened: '',
    description: '',
    location: '',
    dateOccurred: new Date().toISOString().split('T')[0],
    timeOccurred: new Date().toTimeString().slice(0, 5),
    witnesses: ['']
  });

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let photoUrl = null;
      
      if (selectedPhoto) {
        const photoFormData = new FormData();
        photoFormData.append('file', selectedPhoto);
        
        try {
          const photoResponse = await axiosInstance.post('/near-miss/upload-photo', photoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          photoUrl = photoResponse.data.photoUrl;
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          toast.error(t('nearMiss.photoUploadFailed'));
        }
      }

      const cleanedData = {
        ...formData,
        witnesses: formData.witnesses.filter(w => w.trim() !== ''),
        photoUrl: photoUrl
      };

      await axiosInstance.post('/near-miss', cleanedData);
      toast.success(t('nearMiss.reportSubmitted'));
      
      setFormData({
        whatAlmostHappened: '',
        description: '',
        location: '',
        dateOccurred: new Date().toISOString().split('T')[0],
        timeOccurred: new Date().toTimeString().slice(0, 5),
        witnesses: ['']
      });
      setSelectedPhoto(null);
      setPhotoPreview(null);
      
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to submit near miss:', error);
      toast.error(t('nearMiss.submitFailed') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const addWitness = () => {
    setFormData({ ...formData, witnesses: [...formData.witnesses, ''] });
  };

  const updateWitness = (index, value) => {
    const newWitnesses = [...formData.witnesses];
    newWitnesses[index] = value;
    setFormData({ ...formData, witnesses: newWitnesses });
  };

  const removeWitness = (index) => {
    const newWitnesses = formData.witnesses.filter((_, i) => i !== index);
    setFormData({ ...formData, witnesses: newWitnesses });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            {t('nearMiss.quickReportTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('nearMiss.quickReportDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* What Almost Happened */}
          <div className="space-y-2">
            <Label htmlFor="whatAlmostHappened" className="text-sm font-medium">
              {t('nearMiss.whatAlmostHappened')} *
            </Label>
            <Input
              id="whatAlmostHappened"
              data-testid="near-miss-what-happened"
              placeholder={t('nearMiss.whatAlmostHappenedPlaceholder')}
              value={formData.whatAlmostHappened}
              onChange={(e) => setFormData({ ...formData, whatAlmostHappened: e.target.value })}
              required
              className="w-full"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              {t('nearMiss.descriptionLabel')} *
            </Label>
            <Textarea
              id="description"
              data-testid="near-miss-description"
              placeholder={t('nearMiss.descriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              required
              className="w-full"
            />
            <p className="text-xs text-gray-500">
              {t('nearMiss.descriptionHint')}
            </p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-sm font-medium">
              {t('common.location')} *
            </Label>
            <Input
              id="location"
              data-testid="near-miss-location"
              placeholder={t('nearMiss.locationPlaceholder')}
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              required
              className="w-full"
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dateOccurred" className="text-sm font-medium">
                {t('common.date')} *
              </Label>
              <Input
                id="dateOccurred"
                data-testid="near-miss-date"
                type="date"
                value={formData.dateOccurred}
                onChange={(e) => setFormData({ ...formData, dateOccurred: e.target.value })}
                required
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeOccurred" className="text-sm font-medium">
                {t('common.time')} *
              </Label>
              <Input
                id="timeOccurred"
                data-testid="near-miss-time"
                type="time"
                value={formData.timeOccurred}
                onChange={(e) => setFormData({ ...formData, timeOccurred: e.target.value })}
                required
                className="w-full"
              />
            </div>
          </div>

          {/* Photo Upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t('nearMiss.photo')} <span className="text-gray-500 font-normal">({t('common.optional')})</span>
            </Label>
            {photoPreview ? (
              <div className="relative">
                <img 
                  src={photoPreview} 
                  alt="Near miss" 
                  className="w-full h-48 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={removePhoto}
                  className="absolute top-2 right-2"
                  data-testid="near-miss-remove-photo"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('common.remove')}
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <div className="text-gray-600">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="mt-1 text-sm">{t('nearMiss.clickToUploadPhoto')}</p>
                    <p className="text-xs text-gray-500">{t('nearMiss.photoSizeLimit')}</p>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Witnesses */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              {t('nearMiss.witnesses')} <span className="text-gray-500 font-normal">({t('nearMiss.witnessesHint')})</span>
            </Label>
            {formData.witnesses.map((witness, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder={t('nearMiss.witnessName')}
                  value={witness}
                  onChange={(e) => updateWitness(index, e.target.value)}
                  className="flex-1"
                  data-testid={`near-miss-witness-${index}`}
                />
                {formData.witnesses.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeWitness(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addWitness}
              className="w-full"
              data-testid="near-miss-add-witness"
            >
              + {t('nearMiss.addWitness')}
            </Button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>{t('nearMiss.whyReport')}</strong><br />
              {t('nearMiss.whyReportDescription')}
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
              data-testid="near-miss-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
              disabled={loading}
              data-testid="near-miss-submit"
            >
              {loading ? t('nearMiss.submitting') : t('nearMiss.submitReport')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickNearMissReport;
